<template>
    <div class="aa-page">
        <!-- Header -->
        <div class="aa-header">
            <div class="aa-header-left">
                <div class="aa-icon-wrap"><i class="fas fa-hand-holding-box"></i></div>
                <h2 class="aa-title">Asset Allocations</h2>
                <span class="aa-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <div class="aa-header-right">
                <div class="aa-filters">
                    <div class="aa-filter-group">
                        <div class="aa-filter-item">
                            <span class="aa-filter-label">
                                <i class="fas fa-user"></i> Employee
                            </span>
                            <SelectDropdown
                                id="aa-filter-employee"
                                :options="employeeOptions"
                                :value="filterEmployee"
                                placeholder="Select An Option"
                                clearable
                                @change="filterEmployee = $event"
                            />
                        </div>
                        <div class="aa-filter-item">
                            <span class="aa-filter-label">
                                <i class="fas fa-circle"></i> Status
                            </span>
                            <SelectDropdown
                                id="aa-filter-status"
                                :options="statusOptions"
                                :value="filterStatus"
                                placeholder="Select An Option"
                                clearable
                                @change="filterStatus = $event"
                            />
                        </div>
                        <button class="aa-filter-clear" v-if="activeFilters" @click="clearFilters">
                            <i class="fas fa-times"></i> Clear
                        </button>
                    </div>
                </div>
                <AllowPermission :permission="PERMISSION_CODES.EDIT_ASSET_ALLOCATIONS">
                    <button class="aa-add-btn" @click="showForm = true">
                        <i class="fas fa-plus"></i>
                        Allocate Asset
                    </button>
                </AllowPermission>
            </div>
        </div>

        <RightSideModal :show="showForm && !mastersLoading" title="Allocate Asset" @close="closeForm">
            <Form :fields="createFields" title="Asset Allocation" submitUrl="/asset-allocations"
                @success="handleFormSuccess" @cancel="closeForm" :editMode="false" />
        </RightSideModal>

        <!-- Loading -->
        <div v-if="loading" class="aa-loading"><div class="aa-spinner"></div><span>Loading...</span></div>

        <!-- Empty -->
        <div v-else-if="!filteredItems.length" class="aa-empty">
            <div class="aa-empty-icon">
                <i :class="activeFilters ? 'fas fa-filter' : 'fas fa-hand-holding'"></i>
            </div>
            <h3>{{ activeFilters ? 'No results match your filters' : 'No asset allocations found' }}</h3>
        </div>

        <!-- Table -->
        <div v-else class="aa-table-wrap">
            <table class="aa-table">
                <thead>
                    <tr>
                        <th class="th-idx">#</th>
                        <th>Asset</th>
                        <th>Employee</th>
                        <th>Tag</th>
                        <th class="text-center">Allocated Date</th>
                        <th class="text-center">Status</th>
                        <th class="text-center">Returned Date</th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, idx) in filteredItems" :key="item.id">
                        <td class="td-idx">{{ idx + 1 }}</td>
                        <td>
                            <div class="td-cell-flex"><span class="td-asset-icon" :class="aaColor(idx)"><i class="fas fa-box"></i></span><span class="td-name">{{ item.assetName || (item.asset && item.asset.name) || '—' }}</span></div>
                        </td>
                        <td>
                            <div class="td-cell-flex"><span class="td-emp-icon"><i class="fas fa-user"></i></span><span class="td-name">{{ item.employeeName || empName(item.employee) }}</span></div>
                        </td>
                        <td>{{ item.tag || '—' }}</td>
                        <td class="text-center">{{ item.allocatedDateFormatted || '—' }}</td>
                        <td class="text-center">
                            <span class="aa-status" :class="isReturned(item) ? 'aa-status-returned' : 'aa-status-allocated'">
                                <i :class="isReturned(item) ? 'fas fa-undo-alt' : 'fas fa-check-circle'"></i>
                                {{ item.statusName }}
                            </span>
                        </td>
                        <td class="text-center">{{ item.returnedDateFormatted || '—' }}</td>
                        <td class="text-center">
                            <AllowPermission :permission="PERMISSION_CODES.EDIT_ASSET_ALLOCATIONS">
                                <button
                                    class="aa-return-btn"
                                    :class="{ 'aa-return-disabled': isReturned(item) }"
                                    :disabled="isReturned(item) || returning"
                                    @click="openReturnModal(item)"
                                >
                                    <i class="fas fa-undo-alt"></i> Return
                                </button>
                            </AllowPermission>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Return Modal (outside v-if chain) -->
        <transition name="aa-modal-fade">
            <div class="aa-modal-overlay" v-if="returnModal.show" @click.self="closeReturnModal">
                <div class="aa-modal">
                    <div class="aa-modal-header">
                        <div class="aa-modal-icon"><i class="fas fa-undo-alt"></i></div>
                        <div>
                            <h3 class="aa-modal-title">Return Asset</h3>
                            <p class="aa-modal-sub">{{ returnModal.assetName }} &mdash; {{ returnModal.employeeName }}</p>
                        </div>
                        <button class="aa-modal-close" @click="closeReturnModal"><i class="fas fa-times"></i></button>
                    </div>

                    <div class="aa-modal-body">
                        <div class="aa-modal-field">
                            <label class="aa-modal-label"><i class="fas fa-tag"></i> Return Reason <span class="aa-req">*</span></label>
                            <div class="aa-reason-grid">
                                <button
                                    v-for="r in returnReasons"
                                    :key="r.id"
                                    class="aa-reason-btn"
                                    :class="{ 'aa-reason-active': returnModal.reasonId === r.id }"
                                    @click="returnModal.reasonId = r.id"
                                >
                                    <i :class="r.icon"></i>
                                    <span>{{ r.label }}</span>
                                </button>
                            </div>
                        </div>

                        <div class="aa-modal-field">
                            <label class="aa-modal-label"><i class="fas fa-comment-alt"></i> Additional Notes</label>
                            <textarea
                                class="aa-modal-textarea"
                                v-model="returnModal.notes"
                                placeholder="Describe the condition of the asset, any damage, or other relevant details..."
                                rows="3"
                            ></textarea>
                        </div>
                    </div>

                    <div class="aa-modal-footer">
                        <button class="aa-modal-cancel" @click="closeReturnModal">Cancel</button>
                        <button
                            class="aa-modal-confirm"
                            :disabled="!returnModal.reasonId || returning"
                            @click="confirmReturn"
                        >
                            <i class="fas fa-undo-alt"></i>
                            {{ returning ? 'Processing...' : 'Confirm Return' }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

    </div>
</template>

<script>
import { mapState } from "vuex"
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import { PERMISSION_CODES } from "../../store/permission"
import { createField, errMsg } from "../../utils/common"
import { http } from "../../utils/http"

export default {
    data() {
        return {
            createFields: [createField("Tag*", "tag", "text", true)],
            PERMISSION_CODES,
            items: [],
            loading: true,
            mastersLoading: true,
            returning: false,
            initialized: false,
            showForm: false,
            filterEmployee: null,
            filterStatus: null,
            employeeOptions: [],
            statusOptions: [
                { id: "allocated", title: "Allocated" },
                { id: "returned",  title: "Returned"  },
            ],
            returnModal: {
                show: false,
                allocationId: null,
                assetName: "",
                employeeName: "",
                reasonId: null,
                notes: "",
            },
            returnReasons: [
                { id: "not_working",          label: "Not Working / Malfunctioning", icon: "fas fa-exclamation-triangle" },
                { id: "damaged",              label: "Damaged",                       icon: "fas fa-tools" },
                { id: "scrapped",             label: "Asset Scrapped",                icon: "fas fa-trash-alt" },
                { id: "employee_terminated",  label: "Employee Terminated",           icon: "fas fa-user-times" },
                { id: "employee_resigned",    label: "Employee Resigned / Job Left",  icon: "fas fa-sign-out-alt" },
                { id: "upgraded",             label: "Upgraded / Replaced",           icon: "fas fa-sync-alt" },
                { id: "project_completed",    label: "Project Completed",             icon: "fas fa-check-circle" },
                { id: "other",               label: "Other",                         icon: "fas fa-ellipsis-h" },
            ],
        }
    },
    components: { Form, RightSideModal, AllowPermission, SelectDropdown },
    mounted() { this.init() },
    computed: {
        ...mapState("misc", ["companies"]),
        activeFilters() {
            return !!this.filterEmployee || !!this.filterStatus
        },
        filteredItems() {
            return this.items.filter(item => {
                const empMatch = !this.filterEmployee ||
                    String(item.employeeId || (item.employee && item.employee.id)) === String(this.filterEmployee)

                const returned = this.isReturned(item)
                const statusMatch = !this.filterStatus ||
                    (this.filterStatus === "returned"  && returned) ||
                    (this.filterStatus === "allocated" && !returned)

                return empMatch && statusMatch
            })
        },
    },
    methods: {
        aaColor(i) { return ["aa-clr-blue","aa-clr-purple","aa-clr-teal","aa-clr-amber","aa-clr-rose"][i % 5] },
        empName(emp) { if (!emp) return "—"; return `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "—" },
        isReturned(item) {
            return (item.statusName || "").toLowerCase().includes("return") || item.returned === true
        },
        closeForm() { this.showForm = false },
        handleFormSuccess() { this.showForm = false; this.fetchData() },
        clearFilters() {
            this.filterEmployee = null
            this.filterStatus = null
        },
        openReturnModal(item) {
            this.returnModal = {
                show: true,
                allocationId: item.id,
                assetName: item.assetName || (item.asset && item.asset.name) || "Asset",
                employeeName: item.employeeName || this.empName(item.employee),
                reasonId: null,
                notes: "",
            }
        },
        closeReturnModal() {
            this.returnModal.show = false
        },
        async confirmReturn() {
            if (!this.returnModal.reasonId || this.returning) return
            this.returning = true
            const reason = this.returnReasons.find(r => r.id === this.returnModal.reasonId)
            try {
                await http.patch(`/asset-allocations/${this.returnModal.allocationId}/return`, {
                    returnReasonId:    this.returnModal.reasonId,
                    returnReasonLabel: reason ? reason.label : "",
                    returnNotes:       this.returnModal.notes,
                })
                this.$toast.success("Asset returned successfully!")
                this.closeReturnModal()
                await this.fetchData()
            } catch (err) {
                console.log("error in returning asset", err)
                this.$toast.error(errMsg(err))
            }
            this.returning = false
        },
        async init() {
            if (this.initialized) return
            try {
                const { assets, employees } = await this.fetchAssetAllocationMasters()
                this.employeeOptions = employees.map(item => ({
                    id: item.id,
                    title: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
                }))
                this.createFields = [
                    ...this.createFields,
                    createField("Asset Item*", "assetId", "select", true, "", assets.map(item => ({ id: item.id, title: item.name }))),
                    createField("Employee*", "employeeId", "select", true, "", employees.map(item => ({ id: item.id, title: `${item.firstName || ""} ${item.lastName || ""}` }))),
                    createField("Date of allocation*", "allocatedDate", "date", true, new Date().toISOString().slice(0, 10)),
                ]
                this.mastersLoading = false
                await this.fetchData()
                this.initialized = true
            } catch (err) { console.log("error in initializing assets", err) }
        },
        async fetchData() {
            this.loading = true
            try {
                const res = await http.get("/asset-allocations")
                this.items = res.data.result || []
            } catch (e) { console.log(e) }
            this.loading = false
        },
        async fetchAssetAllocationMasters() {
            const res = await http.get("/asset-allocations/masters")
            return res.data.result
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.aa-page { animation: aaFade .3s ease; }
@keyframes aaFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.aa-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.aa-header-left { display:flex; align-items:center; gap:10px; }
.aa-header-right { display:flex; align-items:center; gap:10px; }
.aa-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.aa-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.aa-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.aa-add-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } }

/* Filter Bar */
.aa-filters { display:flex; align-items:center; gap:16px; padding:0; background:transparent; border:none; margin-bottom:0; box-shadow:none; flex-wrap:wrap; }
.aa-filter-group { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.aa-filter-item { display:flex; align-items:center; gap:7px; }
.aa-filter-label { display:inline-flex; align-items:center; gap:5px; font-size:11.5px; font-weight:600; color:$gray500; white-space:nowrap; i { font-size:10px; color:$gray400; } }
.aa-filter-clear { display:inline-flex; align-items:center; gap:4px; padding:5px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; color:$gray400; background:white; cursor:pointer; font-family:inherit; transition:all $transitionFast; i { font-size:9px; } &:hover { background:$gray50; color:$gray600; border-color:$gray300; } }

/* Loading / Empty */
.aa-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.aa-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:aaSpin .7s linear infinite; }
@keyframes aaSpin { to { transform:rotate(360deg); } }
.aa-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.aa-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }

/* Table */
.aa-table-wrap { background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; overflow:hidden; }
.aa-table { width:100%; border-collapse:collapse; }
.aa-table thead { background:$gray50; border-bottom:2px solid $gray100; }
.aa-table th { padding:10px 14px; font-size:11px; font-weight:600; color:$gray500; text-transform:uppercase; letter-spacing:0.3px; white-space:nowrap; }
.aa-table td { padding:10px 14px; font-size:12.5px; color:$gray700; font-weight:500; border-bottom:1px solid $gray100; vertical-align:middle; white-space:nowrap; }
.aa-table tbody tr { transition:background $transitionFast; &:last-child td { border-bottom:none; } &:hover { background:$gray50; } }
.th-idx, .td-idx { width:36px; text-align:center; color:$gray400; font-size:11px; }
.text-center { text-align:center; }

/* Asset / Employee cells */
.td-cell-flex { display:inline-flex; align-items:center; gap:8px; font-weight:600; color:$gray800; }
.td-name { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.td-asset-icon { width:28px; height:28px; border-radius:6px; display:inline-flex; align-items:center; justify-content:center; font-size:11px; flex-shrink:0; }
.aa-clr-blue   { background:#eff6ff; color:#2563eb; }
.aa-clr-purple { background:#f5f3ff; color:#7c3aed; }
.aa-clr-teal   { background:#f0fdfa; color:#0d9488; }
.aa-clr-amber  { background:#fffbeb; color:#d97706; }
.aa-clr-rose   { background:#fff1f2; color:#e11d48; }
.td-emp-icon { width:28px; height:28px; border-radius:$radiusFull; background:$primaryLight; color:$primaryColor; display:inline-flex; align-items:center; justify-content:center; font-size:11px; flex-shrink:0; }

/* Status badge */
.aa-status { display:inline-flex; align-items:center; gap:4px; padding:3px 10px; border-radius:$radiusFull; font-size:10.5px; font-weight:700; letter-spacing:0.2px; i { font-size:9px; } }
.aa-status-allocated { background:#f0fdf4; color:#16a34a; }
.aa-status-returned  { background:$gray50;  color:$gray400; }

/* Return button */
.aa-return-btn { display:inline-flex; align-items:center; gap:4px; padding:5px 12px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; color:#7c3aed; i { font-size:10px; } &:hover { background:#f5f3ff; border-color:rgba(124,58,237,0.15); } }
.aa-return-disabled { opacity:0.4; cursor:not-allowed; pointer-events:none; color:$gray400; }

/* Responsive */
@media(max-width:1200px) { .aa-table-wrap { overflow-x:auto; } }
@media(max-width:768px) {
    .aa-header { flex-direction:column; align-items:stretch; }
    .aa-header-right { justify-content:flex-end; }
    .aa-filter-group { flex-direction:column; align-items:flex-start; }
    .aa-filter-item { width:100%; }
    .aa-filter-clear { align-self:flex-start; }
}

/* ── Return Modal ── */
.aa-modal-overlay {
    position:fixed; inset:0; background:rgba(15,23,42,0.45); backdrop-filter:blur(3px);
    z-index:3000; display:flex; align-items:center; justify-content:center; padding:16px;
}
.aa-modal {
    background:white; border-radius:14px; width:100%; max-width:520px;
    box-shadow:0 24px 60px rgba(0,0,0,0.18); overflow:hidden;
}
.aa-modal-header {
    display:flex; align-items:flex-start; gap:12px; padding:18px 20px 14px;
    border-bottom:1px solid $gray100;
}
.aa-modal-icon {
    width:36px; height:36px; border-radius:8px; background:#faf5ff; color:#7c3aed;
    display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0;
}
.aa-modal-title { font-size:15px; font-weight:700; color:$gray800; margin:0 0 2px; }
.aa-modal-sub   { font-size:12px; color:$gray400; margin:0; font-weight:500; }
.aa-modal-close {
    margin-left:auto; width:28px; height:28px; border:1px solid $gray200; border-radius:6px;
    background:white; color:$gray400; cursor:pointer; display:flex; align-items:center;
    justify-content:center; font-size:11px; flex-shrink:0; transition:all $transitionFast;
    &:hover { background:$gray50; color:$gray600; }
}
.aa-modal-body  { padding:18px 20px; display:flex; flex-direction:column; gap:16px; }
.aa-modal-field { display:flex; flex-direction:column; gap:8px; }
.aa-modal-label {
    font-size:12px; font-weight:600; color:$gray600; display:flex; align-items:center; gap:6px;
    i { font-size:11px; color:$gray400; }
}
.aa-req { color:#dc2626; }

/* Reason grid */
.aa-reason-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:7px; }
.aa-reason-btn {
    display:flex; align-items:center; gap:8px; padding:9px 12px;
    border:1.5px solid $gray200; border-radius:8px; background:white;
    font-size:12px; font-weight:500; color:$gray600; cursor:pointer;
    font-family:inherit; transition:all $transitionFast; text-align:left;
    i { font-size:12px; color:$gray400; flex-shrink:0; transition:color $transitionFast; }
    &:hover { border-color:$gray300; background:$gray50; color:$gray800; }
    &.aa-reason-active {
        border-color:#7c3aed; background:#faf5ff; color:#7c3aed; font-weight:600;
        i { color:#7c3aed; }
    }
}

/* Notes textarea */
.aa-modal-textarea {
    width:100%; border:1.5px solid $gray200; border-radius:8px; padding:9px 12px;
    font-size:12.5px; font-family:inherit; color:$gray700; resize:vertical;
    outline:none; transition:border-color .15s; line-height:1.5;
    &::placeholder { color:$gray400; }
    &:focus { border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,0.08); }
}

/* Footer */
.aa-modal-footer {
    display:flex; justify-content:flex-end; gap:8px;
    padding:14px 20px; border-top:1px solid $gray100; background:$gray50;
}
.aa-modal-cancel {
    padding:7px 16px; border:1px solid $gray200; border-radius:6px; background:white;
    color:$gray600; font-size:12.5px; font-weight:600; cursor:pointer; font-family:inherit;
    transition:all $transitionFast;
    &:hover { background:$gray100; }
}
.aa-modal-confirm {
    display:inline-flex; align-items:center; gap:6px; padding:7px 18px;
    border:none; border-radius:6px; background:#7c3aed; color:white;
    font-size:12.5px; font-weight:600; cursor:pointer; font-family:inherit;
    transition:all $transitionFast;
    i { font-size:11px; }
    &:hover:not(:disabled) { background:#6d28d9; }
    &:disabled { opacity:0.45; cursor:not-allowed; }
}

/* Modal transition */
.aa-modal-fade-enter-active { transition:opacity .2s ease, transform .2s ease; }
.aa-modal-fade-leave-active { transition:opacity .15s ease, transform .15s ease; }
.aa-modal-fade-enter, .aa-modal-fade-leave-to { opacity:0; }
.aa-modal-fade-enter .aa-modal, .aa-modal-fade-leave-to .aa-modal { transform:scale(0.96) translateY(6px); }
</style>